import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material'
import { useDispatch } from 'react-redux'
import { api } from '../../../redux/api';

function UnlockTemplates({ open, onClose, node, selectedNodes, onRefreshTree, urlParams, userSpaceChecked, serverChecked }) {
    const [isUnlocking, setIsUnlocking] = useState(false)
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
    const dispatch = useDispatch();
    const folderTypeMap = {
        WORKFLOW: 'workflows',
        INBOUND: 'inbound_templates',
        OUTBOUND: 'outbound_templates',
    }
    

    // Function to format path based on item type
    const formatItemPath = (item) => {
        if (item.type === 'workflow_group') {
            // For workflow group: just its title
            return item.title
        } else if (item.type === 'template_folder') {
            // For template folder: workflowGroup/template_folder
            return `${item.workflowGroup || 'Unknown'}/${item.title}`
        } else {
            // For template: workflowGroup/templateFolder/template title
            // We need to extract folder name from the item structure
            const folderPath = folderTypeMap[item.type] || ''
            if (folderPath) {
                return `${item.workflowGroup || 'Unknown'}/${folderPath}/${item.title}`
            } else {
                return `${item.workflowGroup || 'Unknown'}/${item.title}`
            }
        }
    }

    // Get the item type text for display
    const getItemTypeText = () => {
        if (!selectedNodes || selectedNodes.length === 0) return 'items'
        
        const types = selectedNodes.map(item => {
            if (item.type === 'workflow_group') return 'workflow group'
            if (item.type === 'template_folder') return 'template folder'
            return 'template'
        })
        
        const uniqueTypes = [...new Set(types)]
        if (uniqueTypes.length === 1) {
            return selectedNodes.length > 1 ? `${uniqueTypes[0]}s` : uniqueTypes[0]
        } else {
            return 'items'
        }
    }

    const handleConfirmUnlock = async () => {
        setIsUnlocking(true)
        
        try {
            // Transform selectedNodes to match API format
            const selectedItems = selectedNodes.map(selectedNode => {
                let name = selectedNode.title;
                let path = '';
                let type = '';

                // Determine type and path based on node type
                if (selectedNode.type === 'workflow_group') {
                    type = 'workflow_group';
                    path = selectedNode.title;
                    name = selectedNode.title;
                } else if (selectedNode.type === 'template_folder') {
                    type = 'template_folder';
                    path = selectedNode?.title === 'workflows' ? selectedNode.workflowGroup : `${selectedNode.workflowGroup}/${selectedNode.title}`;
                    name = selectedNode.title;
                } else {
                    // For template files
                    type = 'file';
                    path = `${urlParams.account}/${selectedNode?.workflowGroup}${selectedNode?.type === 'WORKFLOW' ? '' : `/${folderTypeMap[selectedNode?.type]}`}/${selectedNode?.title}`
                    name = selectedNode.title;
                }

                return {
                    name: name,
                    path: path,
                    type: type
                };
            });

            // Prepare the complete API payload
            const apiPayload = {
                selectedItems: selectedItems,
                userspace: userSpaceChecked || false,
                server: serverChecked || false,
                account: urlParams.account,
                user: urlParams.user
            };
                        
            // Make API call for unlock
            const response = await dispatch(api.endpoints.unLockTemplates.initiate({
                account: urlParams.account,
                data: apiPayload
            }))
            
            // Handle API response
            if (response.data) {
                const { current_state, data } = response.data;
                
                if (current_state === "SUCCESS") {
                    // Count successful and failed items
                    const successCount = data.results?.filter(item => item.status === "success").length || 0;
                    const errorCount = data.results?.filter(item => item.status === "error").length || 0;
                    
                    if (errorCount === 0) {
                        // All items unlocked successfully
                        setSnackbar({
                            open: true,
                            message: `Successfully unlocked ${successCount} item${successCount > 1 ? 's' : ''}`,
                            severity: 'success'
                        });
                    } else {
                        // Some items failed
                        const errorMessages = data.errors || [];
                        setSnackbar({
                            open: true,
                            message: `${successCount} item${successCount > 1 ? 's' : ''} unlocked successfully, ${errorCount} failed: ${errorMessages.join(', ')}`,
                            severity: 'warning'
                        });
                    }
                } else {
                    // API returned error state
                    const errorMessages = data?.errors || ['Unknown error occurred'];
                    setSnackbar({
                        open: true,
                        message: `Failed to unlock items: ${errorMessages.join(', ')}`,
                        severity: 'error'
                    });
                }
                
                // Refresh the tree after API call
                if (onRefreshTree) {
                    onRefreshTree()
                }
            } else if (response.error) {
                // Handle API error
                setSnackbar({
                    open: true,
                    message: `Error unlocking items: ${response.error.message || 'Unknown error'}`,
                    severity: 'error'
                });
            }
            
            // Close the dialog
            onClose()
            
        } catch (error) {
            console.error("Error unlocking items:", error)
            setSnackbar({
                open: true,
                message: `Error unlocking items: ${error.message || 'Unknown error'}`,
                severity: 'error'
            });
        } finally {
            setIsUnlocking(false)
        }
    }

    const handleCancel = () => {
        onClose()
    }

    return (
        <>
        <Dialog
            open={open}
            onClose={!isUnlocking ? onClose : undefined}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle className="dialog-header">
                <div className="title">Confirm Unlock</div>
                <div className="description">
                    Are you sure you want to unlock the following {getItemTypeText()}?
                </div>
            </DialogTitle>
            <DialogContent className="dialog-content" dividers>
                <List dense sx={{ maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    {selectedNodes && selectedNodes.map((item, index) => (
                        <ListItem key={item.key || index} divider={index < selectedNodes.length - 1}>
                            <ListItemText
                                primary={formatItemPath(item)}
                                secondary={`Type: ${item.type === 'workflow_group' ? 'Workflow Group' : 
                                                   item.type === 'template_folder' ? 'Template Folder' : 'Template'}`}
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions className="dialog-actions">
                <Button 
                    onClick={handleCancel} 
                    disabled={isUnlocking}
                    variant="outlined"
                >
                    No
                </Button>
                <Button 
                    onClick={handleConfirmUnlock} 
                    disabled={isUnlocking}
                    variant="outlined"
                    startIcon={isUnlocking ? <CircularProgress size={16} /> : null}
                >
                    {isUnlocking ? 'Unlocking...' : 'Yes'}
                </Button>
            </DialogActions>
        </Dialog>
        
        <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
            <Alert
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                severity={snackbar.severity}
                variant="filled"
                sx={{ width: '100%', maxWidth: '450px', whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
                {snackbar.message}
            </Alert>
        </Snackbar>
        </>
    )
}

export default UnlockTemplates 